import React, { useState, useEffect } from 'react';
import './BookingForm.scss';

const BookingForm = ({ onSubmit, onBack, selectedDate, selectedTimeSlot, selectedService, selectedLocation, selectedPractitioner, timeZone, availabilityPeriod }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: {
      day: '',
      month: '',
      year: '',
    },
    email: '',
    phone: '',
    timeZone: timeZone || '(GMT+08:00) Perth',
    comments: '',
    marketingConsent: false,
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const timeZones = [
    '(GMT+08:00) Perth',
    '(GMT+10:00) Sydney',
    '(GMT+10:00) Melbourne',
    '(GMT+09:30) Adelaide',
    '(GMT+09:30) Darwin',
    '(GMT+10:00) Brisbane',
    '(GMT+08:00) Western Australia',
  ];

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  useEffect(() => {
    // Load saved data from localStorage if rememberMe was checked previously
    const savedData = localStorage.getItem('bookingFormData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(prev => ({
          ...prev,
          ...parsed,
          dateOfBirth: parsed.dateOfBirth || prev.dateOfBirth,
        }));
      } catch (err) {
        console.error('Error loading saved form data:', err);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleDOBChange = (field, value) => {
    setFormData({
      ...formData,
      dateOfBirth: {
        ...formData.dateOfBirth,
        [field]: value,
      },
    });
    
    // Clear DOB errors
    if (errors.dateOfBirth) {
      setErrors({
        ...errors,
        dateOfBirth: '',
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Validate DOB (optional but if provided, all fields should be filled)
    if (formData.dateOfBirth.day || formData.dateOfBirth.month || formData.dateOfBirth.year) {
      if (!formData.dateOfBirth.day || !formData.dateOfBirth.month || !formData.dateOfBirth.year) {
        newErrors.dateOfBirth = 'Please provide complete date of birth';
      } else {
        // Validate date
        const day = parseInt(formData.dateOfBirth.day);
        const month = parseInt(formData.dateOfBirth.month) - 1;
        const year = parseInt(formData.dateOfBirth.year);
        const dob = new Date(year, month, day);
        
        if (dob.getDate() !== day || dob.getMonth() !== month || dob.getFullYear() !== year) {
          newErrors.dateOfBirth = 'Please enter a valid date';
        }
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setSubmitting(true);
      
      // Build DOB date string if provided
      let dobString = null;
      if (formData.dateOfBirth.day && formData.dateOfBirth.month && formData.dateOfBirth.year) {
        const day = parseInt(formData.dateOfBirth.day);
        const month = parseInt(formData.dateOfBirth.month) - 1;
        const year = parseInt(formData.dateOfBirth.year);
        dobString = new Date(year, month, day).toISOString();
      }

      // Save form data if rememberMe is checked
      if (formData.rememberMe) {
        localStorage.setItem('bookingFormData', JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          timeZone: formData.timeZone,
          dateOfBirth: formData.dateOfBirth,
        }));
      } else {
        localStorage.removeItem('bookingFormData');
      }

      const submissionData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: dobString,
        email: formData.email,
        phone: formData.phone,
        timeZone: formData.timeZone,
        comments: formData.comments,
        marketingConsent: formData.marketingConsent,
        rememberMe: formData.rememberMe,
        location: selectedLocation?._id || null,
        locationName: selectedLocation?.name || '',
        serviceType: selectedService?.title || '',
        serviceDuration: selectedService?.duration || 30,
        practitioner: selectedPractitioner?._id || null,
        practitionerName: selectedPractitioner?.name || '',
        availabilityPeriod: availabilityPeriod || '',
      };

      onSubmit(submissionData);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  };

  return (
    <div className="booking-form">
      <div className="header-section">
        <button className="btn-back" onClick={onBack}>
          ← Back
        </button>
        <h2>Your Details</h2>
        <div className="appointment-summary">
          <p><strong>Date:</strong> {formatDate(selectedDate)}</p>
          <p><strong>Time:</strong> {formatTime(selectedTimeSlot)}</p>
          {selectedLocation && <p><strong>Location:</strong> {selectedLocation.name}</p>}
          {selectedService && <p><strong>Service:</strong> {selectedService.title}</p>}
          {selectedPractitioner && <p><strong>Practitioner:</strong> {selectedPractitioner.name}</p>}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">
              First Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={errors.firstName ? 'error' : ''}
              placeholder="Enter your first name"
            />
            {errors.firstName && <span className="error-message">{errors.firstName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="lastName">
              Last Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={errors.lastName ? 'error' : ''}
              placeholder="Enter your last name"
            />
            {errors.lastName && <span className="error-message">{errors.lastName}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="dateOfBirth">
            Date of Birth (Optional)
          </label>
          <div className="dob-selectors">
            <select
              value={formData.dateOfBirth.day}
              onChange={(e) => handleDOBChange('day', e.target.value)}
              className={errors.dateOfBirth ? 'error' : ''}
            >
              <option value="">Day</option>
              {days.map(day => (
                <option key={day} value={day}>{day}</option>
              ))}
            </select>
            <select
              value={formData.dateOfBirth.month}
              onChange={(e) => handleDOBChange('month', months.indexOf(e.target.value) + 1)}
              className={errors.dateOfBirth ? 'error' : ''}
            >
              <option value="">Month</option>
              {months.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
            <select
              value={formData.dateOfBirth.year}
              onChange={(e) => handleDOBChange('year', e.target.value)}
              className={errors.dateOfBirth ? 'error' : ''}
            >
              <option value="">Year</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="your.email@example.com"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              Phone Number <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={errors.phone ? 'error' : ''}
              placeholder="(08) 1234 5678"
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="timeZone">
            Time Zone
          </label>
          <select
            id="timeZone"
            name="timeZone"
            value={formData.timeZone}
            onChange={handleChange}
          >
            {timeZones.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="comments">Additional Comments (Optional)</label>
          <textarea
            id="comments"
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            rows={4}
            placeholder="Any additional information you'd like to share"
          />
        </div>

        <div className="form-checkboxes">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="marketingConsent"
              name="marketingConsent"
              checked={formData.marketingConsent}
              onChange={handleChange}
            />
            <label htmlFor="marketingConsent">
              I consent to receive marketing communications
            </label>
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="rememberMe"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
            />
            <label htmlFor="rememberMe">
              Remember my details for next time
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="dashboard-btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button type="submit" className="dashboard-btn btn-primary" disabled={submitting}>
            {submitting ? 'Processing...' : 'Confirm Booking'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BookingForm;
